import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTranslation } from '../languages';
import { ArrowRight } from 'lucide-react';

export default function GuidedTutorial({ isActive, onDismiss, onComplete, uiLanguage = 'en' }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [spotlightPosition, setSpotlightPosition] = useState(null);
    const [showContent, setShowContent] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const tutorialRef = useRef(null);
    
    const t = (key) => getTranslation(key, uiLanguage);
    const totalSteps = 2;

    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setShowContent(false);
        setTimeout(() => {
            setIsExiting(false);
            onDismiss();
        }, 300);
    }, [onDismiss]);

    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            setShowContent(false);
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
            }, 300);
        }
    }, [currentStep, totalSteps]);

    const handleComplete = useCallback(() => {
        setIsExiting(true);
        setShowContent(false);
        setTimeout(() => {
            setIsExiting(false);
            setCurrentStep(1); // Reset for next time
            onComplete();
        }, 300);
    }, [onComplete]);

    useEffect(() => {
        if (isActive) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                // Determine which button to spotlight based on current step (swapped order)
                const targetSelector = currentStep === 1 
                    ? '[data-tutorial-target="add-topic-button"]'
                    : '[data-tutorial-target="tutorial-button"]';
                
                const targetButton = document.querySelector(targetSelector);
                if (targetButton) {
                    const rect = targetButton.getBoundingClientRect();
                    const scrollY = window.scrollY;
                    
                    setSpotlightPosition({
                        top: rect.top + scrollY,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        centerX: rect.left + rect.width / 2,
                        centerY: rect.top + rect.height / 2 + scrollY
                    });
                    
                    // Show content after spotlight is positioned
                    setTimeout(() => setShowContent(true), 300);
                }
            }, 100);
            
            return () => clearTimeout(timer);
        } else {
            setShowContent(false);
            setSpotlightPosition(null);
        }
    }, [isActive, currentStep]);

    useEffect(() => {
        // Handle escape key
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isActive) {
                handleDismiss();
            }
        };

        if (isActive) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent scrolling while tutorial is active
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isActive, handleDismiss]);

    if (!isActive && !isExiting) return null;

    return (
        <div 
            ref={tutorialRef}
            className={`fixed inset-0 z-[60] transition-all duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
        >
            {/* Dark overlay with rectangular hole for spotlight */}
            <svg 
                className="absolute inset-0 w-full h-full"
                style={{ zIndex: 1 }}
            >
                <defs>
                    <mask id="spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {spotlightPosition && (
                            <rect
                                x={spotlightPosition.left - 10}
                                y={spotlightPosition.top - 10}
                                width={spotlightPosition.width + 20}
                                height={spotlightPosition.height + 20}
                                rx="12"
                                fill="black"
                                className="transition-all duration-500 ease-out"
                            />
                        )}
                    </mask>
                </defs>
                
                <rect 
                    x="0" 
                    y="0" 
                    width="100%" 
                    height="100%" 
                    fill="rgba(17, 24, 39, 0.75)" 
                    mask="url(#spotlight-mask)"
                />
            </svg>
            
            {/* Spotlight effect */}
            {spotlightPosition && (
                <>
                    {/* Rectangle border around spotlight */}
                    <div 
                        className={`absolute transition-all duration-500 ease-out pointer-events-none ${showContent ? 'opacity-100' : 'opacity-0'}`}
                        style={{
                            top: spotlightPosition.top - 10,
                            left: spotlightPosition.left - 10,
                            width: spotlightPosition.width + 20,
                            height: spotlightPosition.height + 20,
                            borderRadius: '12px',
                            background: 'transparent',
                            border: '3px solid rgba(14, 165, 233, 0.8)',
                            boxShadow: '0 0 30px rgba(14, 165, 233, 0.5), inset 0 0 20px rgba(14, 165, 233, 0.3)',
                            animation: 'pulse-glow 2s ease-in-out infinite',
                            zIndex: 2
                        }}
                    />
                </>
            )}
            
            {/* Tutorial content card */}
            {spotlightPosition && (
                <div 
                    className={`absolute transition-all ${
                        showContent 
                            ? 'opacity-100 scale-100 translate-y-0' 
                            : 'opacity-0 scale-95 translate-y-4'
                    }`}
                    style={{
                        top: spotlightPosition.top + spotlightPosition.height + 30,
                        left: Math.max(20, Math.min(spotlightPosition.centerX - 200, window.innerWidth - 420)),
                        transition: 'all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transitionDelay: showContent ? '0.2s' : '0s',
                        pointerEvents: 'auto',
                        width: 400,
                        maxWidth: 'calc(100vw - 40px)',
                        zIndex: 3
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative">
                        {/* Content card */}
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl overflow-hidden border border-gray-200/50">
                            {/* Header - matching TutorialMode */}
                            <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-white">
                                        {currentStep === 1 
                                            ? (t('tutorialWelcomeTitle') || "Welcome to Daily Learning Hub!")
                                            : (t('tutorialButtonSpotlightTitle') || "Interactive Tutorial")}
                                    </h3>
                                    <span className="text-sm text-white/90 font-medium">
                                        {t('step')} {currentStep} / {totalSteps}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Content section */}
                            <div className="p-6">
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        {currentStep === 1
                                            ? (t('tutorialWelcomeMessage') || "Let's start your personalized learning journey! Click the blue '+' button above to add your first topic - anything you want to learn about.")
                                            : (t('tutorialButtonSpotlightMessage') || "Great! Before you dive in, would you like a comprehensive walkthrough of all features? Click the Tutorial button to explore everything this app can do.")}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Footer section - matching TutorialMode */}
                            <div className="bg-gradient-to-b from-gray-50 to-white px-6 py-4 flex items-center justify-between border-t border-gray-100">
                                {currentStep === 2 && (
                                    <button
                                        onClick={handleComplete}
                                        className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                                    >
                                        {t('tutorialSkip') || 'Skip Tour'}
                                    </button>
                                )}
                                <div className={`flex items-center gap-2 ${currentStep === 2 ? 'ml-auto' : 'w-full justify-end'}`}>
                                    <button
                                        onClick={currentStep === 1 ? handleNext : handleComplete}
                                        className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                                    >
                                        {currentStep === 1 
                                            ? (t('next') || "Next")
                                            : (t('tutorialGetStarted') || "Take the Tour")}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Add custom animation styles */}
            <style jsx>{`
                @keyframes pulse-glow {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.05);
                        opacity: 0.8;
                    }
                }
                
                @keyframes animate-pulse-slow {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.7;
                    }
                }
                
                .animate-pulse-slow {
                    animation: animate-pulse-slow 3s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
}